import { type CSSProperties, type ReactNode } from "react";
import { TypewriterText } from "../../pages/AboutPage/TypewriterText";

export type ChapterData = {
  id: string;
  label: string;
  headline: string;
  subhead: string;
  body: string[];
};

type ChapterSectionProps = {
  chapter: ChapterData;
  index: number;
  isActive: boolean;
  isVisible: boolean;
  isPageReady: boolean;
  isFirstChapter: boolean;
  isLastChapter: boolean;
  typewriterDone: boolean;
  reducedMotion: boolean;
  hasScrolled: boolean;
  onTypewriterDone: (part: "headline" | "subhead") => void;
  footer?: ReactNode;
  className?: string;
};

export const ChapterSection = ({
  chapter,
  index,
  isActive,
  isVisible,
  isPageReady,
  isFirstChapter,
  isLastChapter,
  typewriterDone,
  reducedMotion,
  hasScrolled,
  onTypewriterDone,
  footer,
  className,
}: ChapterSectionProps) => {
  return (
    <section
      id={chapter.id}
      className={`scroll-chapter${isActive ? " is-active" : ""}${isVisible ? " is-visible" : ""}${typewriterDone ? " is-typewriter-done" : ""}${className ? ` ${className}` : ""}`}
      data-chapter-id={chapter.id}
      data-chapter-label={chapter.label}
      aria-labelledby={`${chapter.id}-headline`}
    >
      <div className="scroll-chapter__content">
        <div className="scroll-chapter__left">
          <span className="scroll-chapter__mark" aria-hidden="true">
            <span className="scroll-chapter__mark-dot" />
            <span className="scroll-chapter__mark-line" />
          </span>
          <p className="scroll-chapter__eyebrow">{chapter.label}</p>
          <TypewriterText
            variant="headline"
            id={`${chapter.id}-headline`}
            text={chapter.headline}
            active={isVisible && isPageReady}
            reducedMotion={reducedMotion}
            onDone={() => onTypewriterDone("headline")}
          />
          <TypewriterText
            variant="subhead"
            text={chapter.subhead}
            active={isVisible && isPageReady}
            reducedMotion={reducedMotion}
            onDone={() => onTypewriterDone("subhead")}
          />
        </div>
        {chapter.body.length > 0 && (
          <div className="scroll-chapter__right">
            <ul
              className="scroll-chapter__list"
              aria-label={`${chapter.label} details`}
            >
              {chapter.body.map((line, lineIndex) => (
                <li
                  key={line}
                  className="scroll-chapter__line"
                  style={
                    {
                      "--line-delay": `${180 + lineIndex * 95}ms`,
                    } as CSSProperties
                  }
                >
                  {line}
                </li>
              ))}
            </ul>
          </div>
        )}
        {isFirstChapter ? (
          <p
            className={`scroll-chapter__scroll-hint${hasScrolled ? " is-hidden" : ""}`}
            aria-hidden="true"
          >
            Scroll down
          </p>
        ) : null}
      </div>
      {isLastChapter && footer ? footer : null}
    </section>
  );
};
